import React, { ReactNode } from 'react';
import PropTypes from 'prop-types';
import { st, classes } from './Header.st.css';
import ModalHeading, { ModalHeadingProps } from './ModalHeading';
import { dataHooks } from '../../constants';
import Text from '../../../Text';
import Divider from '../../../Divider';
import { useBaseModalLayoutContext } from '../../BaseModalLayoutContext';
import { ariaLabels } from '../../../common/accessibility/ariaLabels';
import { HeaderProps } from './Header.types';

export const Header = ({
  dataHook,
  className,
  showHeaderDivider = false,
  titleSize,
  titleTag,
}: HeaderProps) => {
  const { headerClassName, title, subtitle } = useBaseModalLayoutContext();

  return (
    ((title || subtitle) && (
      <div
        data-hook={dataHook}
        className={st(classes.root, headerClassName, className)}
      >
        <div className={classes.innerContent}>
          {typeof title === 'string' ? (
            <Header.Title titleSize={titleSize} titleTag={titleTag}>
              {title}
            </Header.Title>
          ) : (
            title
          )}
          {subtitle && (
            <Text
              className={classes.subtitle}
              secondary
              dataHook={dataHooks.headerSubtitle}
              id={ariaLabels.describedBy}
            >
              {subtitle}
            </Text>
          )}
        </div>
        {showHeaderDivider && <Divider />}
      </div>
    )) ||
    null
  );
};

Header.Title = ({
  titleSize,
  titleTag,
  children,
}: {
  titleSize?: ModalHeadingProps['size'];
  titleTag?: ModalHeadingProps['as'];
  children?: ReactNode;
}) => (
  <ModalHeading
    size={titleSize}
    as={titleTag}
    children={children}
    className={classes.title}
    dataHook={dataHooks.headerTitle}
    id={ariaLabels.labelledBy}
  />
);

Header.displayName = 'BaseModalLayout.Header';

Header.propTypes = {
  /** Specifies a CSS class name to be appended to the component’s root element.
   * @internal
   */
  className: PropTypes.string,

  /** data hook for testing */
  dataHook: PropTypes.string,

  /** The modal's title */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  /** The modal's subtitle */
  subtitle: PropTypes.string,

  /** Shows a divider at the bottom of the Header*/
  showHeaderDivider: PropTypes.bool,
};
